import type {
  Identified
} from "../shared/kernel/entity.js";
import type { UtcDateTime } from "../shared/kernel/date-time.js";
import type {
  AuditLogId,
  ClientId,
  CorrelationId,
  ProfessionalId,
  UserId
} from "../shared/kernel/identifiers.js";
import type { JsonObject } from "../shared/kernel/json.js";

export enum AuditAction {
  Create = "CREATE",
  Update = "UPDATE",
  Delete = "DELETE",
  Inactivate = "INACTIVATE",
  StatusChange = "STATUS_CHANGE",
  Approval = "APPROVAL",
  ReturnForCorrection = "RETURN_FOR_CORRECTION",
  Signature = "SIGNATURE",
  Publication = "PUBLICATION",
  Calculation = "CALCULATION",
  DocumentGeneration = "DOCUMENT_GENERATION",
  DocumentIssued = "DOCUMENT_ISSUED",
  // Bloco 03 — Documentação e Biblioteca: TechnicalDocumentService.
  TechnicalDocumentUploaded = "TECHNICAL_DOCUMENT_UPLOADED",
  TechnicalDocumentRevised = "TECHNICAL_DOCUMENT_REVISED",
  TechnicalDocumentInactivated = "TECHNICAL_DOCUMENT_INACTIVATED",
  TechnicalDocumentDownloaded = "TECHNICAL_DOCUMENT_DOWNLOADED",
  /** Bloco 04: importação em lote de Sistemas/Tubulações. */
  EquipmentImportExecuted = "EQUIPMENT_IMPORT_EXECUTED",
  ClientChange = "CLIENT_CHANGE",
  Login = "LOGIN",
  StudioValidate = "STUDIO_VALIDATE",
  StudioTest = "STUDIO_TEST",
  StudioPublish = "STUDIO_PUBLISH",
  StudioInactivate = "STUDIO_INACTIVATE",
  StudioArchive = "STUDIO_ARCHIVE",
  StudioClone = "STUDIO_CLONE",
  EngineeringAnalysisStarted = "ENGINEERING_ANALYSIS_STARTED",
  EngineeringRecommendationEdited = "ENGINEERING_RECOMMENDATION_EDITED",
  EngineeringCorrectionRequested = "ENGINEERING_CORRECTION_REQUESTED",
  EngineeringCorrectionDelegated = "ENGINEERING_CORRECTION_DELEGATED",
  InspectorCorrectionSubmitted = "INSPECTOR_CORRECTION_SUBMITTED",
  EngineeringCorrectionValidated = "ENGINEERING_CORRECTION_VALIDATED",
  EngineeringAnalysisCompleted = "ENGINEERING_ANALYSIS_COMPLETED",
  EngineeringMinutaValidated = "ENGINEERING_MINUTA_VALIDATED",
  MinutaPlhApproved = "MINUTA_PLH_APPROVED",
  MinutaReturnedForCorrection = "MINUTA_RETURNED_FOR_CORRECTION",
  IntegrityReportValidated = "INTEGRITY_REPORT_VALIDATED",
  AvrSubmittedForPlhReview = "AVR_SUBMITTED_FOR_PLH_REVIEW",
  AvrResubmittedForPlhReview = "AVR_RESUBMITTED_FOR_PLH_REVIEW",
  AvrPlhReviewStarted = "AVR_PLH_REVIEW_STARTED",
  AvrPlhCorrectionRequested = "AVR_PLH_CORRECTION_REQUESTED",
  AvrPlhReturned = "AVR_PLH_RETURNED",
  AvrPlhReviewApproved = "AVR_PLH_REVIEW_APPROVED",
  AvrPointHistoryChanged = "AVR_POINT_HISTORY_CHANGED",
  AvrDeleted = "AVR_DELETED",
  DocumentSubmittedToPlh = "DOCUMENT_SUBMITTED_TO_PLH",
  DocumentPlhReviewStarted = "DOCUMENT_PLH_REVIEW_STARTED",
  DocumentPlhCorrectionRequested = "DOCUMENT_PLH_CORRECTION_REQUESTED",
  DocumentResubmittedToPlh = "DOCUMENT_RESUBMITTED_TO_PLH",
  DocumentPlhApproved = "DOCUMENT_PLH_APPROVED",
  DocumentPlhSigned = "DOCUMENT_PLH_SIGNED",
  // Painel de Engenharia — correção por item, HOLD, Minuta e corretor ortográfico.
  MinuteCreated = "MINUTE_CREATED",
  MinuteEdited = "MINUTE_EDITED",
  CorrectionRequested = "CORRECTION_REQUESTED",
  CorrectionResolved = "CORRECTION_RESOLVED",
  DocumentPutOnHold = "DOCUMENT_PUT_ON_HOLD",
  HoldEngineeringCommented = "HOLD_ENGINEERING_COMMENTED",
  DocumentRemovedFromHold = "DOCUMENT_REMOVED_FROM_HOLD",
  SpellingCorrectionApplied = "SPELLING_CORRECTION_APPLIED",
  DocumentFinalized = "DOCUMENT_FINALIZED",
  AvrRejected = "AVR_REJECTED",
  IntegrityAssessmentUpdated = "INTEGRITY_ASSESSMENT_UPDATED",
  // Zona de Risco — Reset de Banco de Dados com Backup
  DatabaseResetRequested = "DATABASE_RESET_REQUESTED",
  DatabaseBackupCreated = "DATABASE_BACKUP_CREATED",
  DatabaseResetCompleted = "DATABASE_RESET_COMPLETED",
  DatabaseResetFailed = "DATABASE_RESET_FAILED",
  // Pessoas e Acessos — reconciliação do vínculo User ↔ Professional.
  PersonIdentityRelinked = "PERSON_IDENTITY_RELINKED",
  // Substituição do PLH responsável do Cliente e transferência de pendentes.
  ClientPlhReplaced = "CLIENT_PLH_REPLACED",
  DocumentPlhReassigned = "DOCUMENT_PLH_REASSIGNED",
  // Controle de Campo — avanço das inspeções por Linha e vínculo Inspetor/Sistema.
  FieldProgressUpdated = "FIELD_PROGRESS_UPDATED",
  FieldLineMarkedOutOfOperation = "FIELD_LINE_MARKED_OUT_OF_OPERATION",
  FieldLineReturnedToOperation = "FIELD_LINE_RETURNED_TO_OPERATION",
  FieldInspectorAssigned = "FIELD_INSPECTOR_ASSIGNED",
  FieldInspectorUnassigned = "FIELD_INSPECTOR_UNASSIGNED",
  // Editor de imagem — versão anotada gerada/descartada (a original nunca muda).
  ImageAnnotationSaved = "IMAGE_ANNOTATION_SAVED",
  ImageAnnotationRestored = "IMAGE_ANNOTATION_RESTORED",
  // ME em alta temperatura — MÉTODO A (aproximado), substitui o Método B (N-2371).
  MethodAThicknessApproximated = "METHOD_A_THICKNESS_APPROXIMATED",
  MethodAMissingData = "METHOD_A_MISSING_DATA",
  /** @deprecated Método B (N-2371) removido — mantido só para ler auditoria antiga. */
  HotCalibrationCreated = "HOT_THICKNESS_CALIBRATION_CREATED",
  /** @deprecated */
  HotCalibrationVerified = "HOT_THICKNESS_CALIBRATION_VERIFIED",
  /** @deprecated */
  HotCalibrationInactivated = "HOT_THICKNESS_CALIBRATION_INACTIVATED",
  /** @deprecated */
  MethodBCalibrationSelected = "METHOD_B_CALIBRATION_SELECTED",
  /** @deprecated */
  CorrectionFactorCalculated = "CORRECTION_FACTOR_CALCULATED",
  /** @deprecated */
  HotThicknessCorrected = "HOT_THICKNESS_CORRECTED",
  /** @deprecated */
  CalibrationNotFound = "CALIBRATION_NOT_FOUND",
  /** @deprecated */
  CalibrationInvalid = "CALIBRATION_INVALID",
  /** @deprecated */
  ReinspectionRequired = "REINSPECTION_REQUIRED",
  // INSPECT IA REVIEW (substitui o Córtex).
  AiReviewStarted = "AI_REVIEW_STARTED",
  AiReviewCompleted = "AI_REVIEW_COMPLETED",
  AiReviewFailed = "AI_REVIEW_FAILED",
  AiFindingCreated = "AI_FINDING_CREATED",
  AiCorrectionApplied = "AI_CORRECTION_APPLIED",
  AiCorrectionRejected = "AI_CORRECTION_REJECTED",
  AiTechnicalWarningAccepted = "AI_TECHNICAL_WARNING_ACCEPTED",
  AiTechnicalWarningJustified = "AI_TECHNICAL_WARNING_JUSTIFIED",
  AiSettingsUpdated = "AI_SETTINGS_UPDATED",
  AiSubmittedWithoutReview = "AI_SUBMITTED_WITHOUT_REVIEW",
  // Administração — Ferramenta Central de Backup e Restauração.
  BackupCreated = "BACKUP_CREATED",
  BackupRestored = "BACKUP_RESTORED",
  BackupDeleted = "BACKUP_DELETED",
  BackupSafetySnapshotCreated = "BACKUP_SAFETY_SNAPSHOT_CREATED",
  BackupRetentionExecuted = "BACKUP_RETENTION_EXECUTED",
  BackupSettingsUpdated = "BACKUP_SETTINGS_UPDATED"
}

export interface AuditLog extends Identified<AuditLogId> {
  readonly clientId: ClientId | null;
  readonly correlationId: CorrelationId;
  readonly userId: UserId;
  readonly professionalId: ProfessionalId | null;
  readonly action: AuditAction;
  readonly entityType: string;
  readonly entityId: string;
  readonly previousState: JsonObject | null;
  readonly newState: JsonObject | null;
  readonly metadata: JsonObject;
  readonly timestamp: UtcDateTime;
}
